package no.vareflow.app.domain

/**
 * One company (tenant). Everything a company owns lives under companies/{id} in Firestore; the
 * name, short name and logo are shown in the header so people always see which company they are in.
 */
data class CompanyProfile(
    val id: String,
    val name: String,
    val shortName: String = "",
    /** Firmakode: short public code for e-mail-less employees and helper links, e.g. "HBV24". */
    val code: String = "",
    val status: CompanyStatus = CompanyStatus.ACTIVE,
    /** Header: logo, name or both (Bedriftsprofil). Without a logo the name is always shown. */
    val headerDisplay: HeaderDisplay = HeaderDisplay.LOGO
) {
    /** The header shows the logo (when there is one). */
    fun showsLogo(hasLogo: Boolean): Boolean = hasLogo && headerDisplay != HeaderDisplay.NAME
    /** The header shows the company name (always when there is no logo). */
    fun showsName(hasLogo: Boolean): Boolean = !hasLogo || headerDisplay != HeaderDisplay.LOGO

    /** What the header shows: the short name when there is one, else the full name. */
    val displayName: String get() = shortName.ifBlank { name }.ifBlank { id }
    val monogram: String get() = CompanyCode.monogram(name.ifBlank { shortName })
}

enum class CompanyStatus { ACTIVE, SUSPENDED }

/**
 * What the header shows when the company has a logo. Most logos contain the name, so the logo alone
 * is the default; without a logo the name is shown whatever is chosen here.
 */
enum class HeaderDisplay(private val nb: String, private val en: String) {
    LOGO("Bare logo", "Logo only"),
    NAME("Bare navn", "Name only"),
    BOTH("Logo og navn", "Logo and name");

    val label: String get() = t(nb, en)

    companion object {
        fun fromCloud(value: Any?): HeaderDisplay = entries.firstOrNull { it.name == value?.toString() } ?: LOGO
    }
}

/**
 * Firmakode rules: 4–6 characters, A–Z and the digits 2–9. 0 and 1 are never used, so a typed
 * "0" or "1" is read as the letter O or I and nobody has to guess which one was meant.
 * Case, spaces and dashes do not matter: " hbv-24 " is HBV24.
 */
object CompanyCode {
    const val MIN_LENGTH = 4
    const val MAX_LENGTH = 6
    /** Same pattern as in firestore.rules. */
    const val PATTERN = "^[A-Z2-9]{4,6}$"
    private val pattern = Regex(PATTERN)
    private const val DIGITS = "23456789"

    /** Legal forms that say nothing about which company it is. */
    private val legalSuffixes = setOf("AS", "ASA", "DA", "ANS", "ENK", "SA", "BA", "NUF", "AB", "KS", "IKS", "SUS")

    fun normalize(input: String): String =
        input.uppercase()
            .filterNot { it.isWhitespace() || it == '-' || it == '_' || it == '.' }
            .replace('0', 'O')
            .replace('1', 'I')

    fun isValid(code: String): Boolean = pattern.matches(code)

    /**
     * A code made from the company name: the initials (or the first letters of a one-word name)
     * plus digits, e.g. "Hønefoss Byggvare AS" → "HB" + "24". [taken] are codes already in use;
     * the result is never one of them. The same name always gives the same first suggestion.
     */
    fun suggest(companyName: String, taken: Set<String> = emptySet()): String {
        val fullBase = baseFor(companyName)
        val start = companyName.fold(7) { acc, c -> (acc * 31 + c.code) and 0x7fffffff }
        // Prefer the whole base with two digits, then one digit; if all of those are taken, use a
        // shorter base with more digits.
        for (baseLength in fullBase.length downTo 1) {
            val base = fullBase.take(baseLength)
            val digitCounts = (MIN_LENGTH - baseLength).coerceAtLeast(1)..(MAX_LENGTH - baseLength)
            for (pad in digitCounts.sortedBy { if (it == 2) 0 else it }) {
                val total = pow(DIGITS.length, pad)
                for (i in 0 until total) {
                    val candidate = base + digits(((start % total) + i) % total, pad)
                    if (isValid(candidate) && candidate !in taken) return candidate
                }
            }
        }
        error("No free Firmakode") // 8^5 codes per letter: never happens in practice
    }

    /** Letters for the badge shown when a company has no logo: "Hønefoss Byggvare AS" → "HB". */
    fun monogram(companyName: String): String {
        val words = words(companyName)
        return when {
            words.isEmpty() -> "V"
            words.size == 1 -> words[0].take(2)
            else -> "${words[0][0]}${words[1][0]}"
        }
    }

    private fun baseFor(companyName: String): String {
        val words = words(companyName)
        return when {
            words.isEmpty() -> "VF"
            words.size == 1 -> words[0].take(4)
            else -> words.take(3).joinToString("") { it.take(1) }
        }
    }

    /** Upper-case ASCII words of the name, without legal suffixes. "Peters Kafé AS" → [PETERS, KAFE]. */
    private fun words(companyName: String): List<String> =
        companyName.uppercase()
            .replace("Æ", "AE").replace("Ø", "O").replace("Å", "A")
            .replace("Ä", "A").replace("Ö", "O").replace("Ü", "U")
            .replace("É", "E").replace("È", "E").replace("Á", "A")
            .split(Regex("[^A-Z0-9]+"))
            .map { word -> word.filter { it in 'A'..'Z' } }
            .filter { it.isNotEmpty() && it !in legalSuffixes }

    private fun digits(value: Int, width: Int): String {
        var v = value
        val out = CharArray(width)
        for (i in width - 1 downTo 0) {
            out[i] = DIGITS[v % DIGITS.length]
            v /= DIGITS.length
        }
        return out.concatToString()
    }

    private fun pow(base: Int, exp: Int): Int = (1..exp).fold(1) { acc, _ -> acc * base }
}

/**
 * The company's logo (Mer → Bedrift). Stored in Firestore companies/{id}/branding/logo, because the
 * Spark plan has no Cloud Storage: a PNG for the screens (keeps transparency) and a JPEG for PDF/Excel
 * reports, both small (at most [MAX_SIDE] pixels on the longest side).
 *
 * [darkBackground]: the logo is light (e.g. white text on a transparent background), so it is shown on
 * a dark badge ([DARK_BACKGROUND_CSS]) and its JPEG is made on that colour instead of white.
 */
class CompanyLogo(
    val png: ByteArray,
    val jpeg: ByteArray,
    val width: Int,
    val height: Int,
    val darkBackground: Boolean = false
) {
    override fun equals(other: Any?): Boolean =
        other is CompanyLogo && width == other.width && height == other.height && darkBackground == other.darkBackground &&
            png.contentEquals(other.png) && jpeg.contentEquals(other.jpeg)
    override fun hashCode(): Int = png.contentHashCode() * 31 + jpeg.contentHashCode() + if (darkBackground) 1 else 0

    companion object {
        /** Longest side after scaling, in pixels. */
        const val MAX_SIDE = 512
        /** Each image as Base64 must stay under this (two of them fit in one Firestore document). */
        const val MAX_BASE64_CHARS = 400_000
        /** The dark badge behind light logos (screens and reports). */
        const val DARK_BACKGROUND_RGB = 0x2B2A28
        const val DARK_BACKGROUND_CSS = "#2B2A28"

        /** Why this logo cannot be saved, or null when it is fine. */
        fun problem(logo: CompanyLogo): String? = when {
            logo.width <= 0 || logo.height <= 0 || logo.png.isEmpty() || logo.jpeg.isEmpty() ->
                t("Bildet kunne ikke leses.", "The image could not be read.")
            (logo.png.size + 2) / 3 * 4 > MAX_BASE64_CHARS || (logo.jpeg.size + 2) / 3 * 4 > MAX_BASE64_CHARS ->
                t("Logoen er for stor. Prøv et enklere bilde (PNG eller JPG).", "The logo is too large. Try a simpler image (PNG or JPG).")
            else -> null
        }
    }
}

/**
 * Pixel rules shared by the web and the phone when a logo is read, so both give the same result.
 */
object LogoAnalysis {
    /** At least this share of transparent pixels: a logo "on a transparent background". */
    private const val TRANSPARENT_SHARE = 0.10
    /** At least this share of the visible pixels light: the logo needs a dark background. */
    private const val LIGHT_SHARE = 0.25

    fun isTransparent(alpha: Int): Boolean = alpha < 16
    fun isVisible(alpha: Int): Boolean = alpha >= 128
    fun isLight(r: Int, g: Int, b: Int): Boolean = 0.2126 * r + 0.7152 * g + 0.0722 * b >= 215.0

    fun hasTransparentBackground(transparentPixels: Int, totalPixels: Int): Boolean =
        totalPixels > 0 && transparentPixels >= totalPixels * TRANSPARENT_SHARE

    /**
     * Empty margin to trim. On a transparent logo only transparency is empty (white text in it must
     * stay); on an opaque picture near-white is empty too.
     */
    fun isEmpty(alpha: Int, r: Int, g: Int, b: Int, transparentBackground: Boolean): Boolean =
        isTransparent(alpha) || (!transparentBackground && r > 244 && g > 244 && b > 244)

    /** True for e.g. white text on a transparent background, which would vanish on white. */
    fun needsDarkBackground(transparentPixels: Int, visiblePixels: Int, lightVisiblePixels: Int, totalPixels: Int): Boolean =
        hasTransparentBackground(transparentPixels, totalPixels) && visiblePixels > 0 &&
            lightVisiblePixels >= visiblePixels * LIGHT_SHARE
}
